/**
 * Budget end-to-end unico per un turno della chat Martina.
 *
 * `deadlineAt` (ora + TOTAL_BUDGET) e' il confine visibile del turno. Il
 * provider viene abortito PRIMA, a `interactiveProviderCutoffAt`, cosi' restano
 * MARTINA_TURN_SERVER_RESPONSE_RESERVE_MS per fallback locale e persistenza
 * del run. Il client non uccide l'HTTP a 90s: il polling del run durevole
 * continua almeno fino a deadlineAt + CLIENT_RESPONSE_GRACE_MS per leggere
 * l'esito persistito. Stop utente resta un abort distinto, non una deadline.
 */
// Kimi K2.6 ha mostrato 43s per il passaggio native-tool e una seconda
// chiamata di sintesi nello stesso turno; 60s lasciava troppo poco margine.
// Il turno resta bounded, ma consente una sintesi provider completa senza
// trasformare una risposta lenta in una falsa modalità offline.
export const MARTINA_TURN_TOTAL_BUDGET_MS = 90_000;
/**
 * Finestra massima della fase nativa provider/tool dentro il turno. Deve
 * essere piu' corta del turno complessivo: dopo una chiamata lenta restano
 * tempo e controllo per il fallback locale, la persistenza e il polling.
 */
// Le sintesi reali di Martina hanno completamenti osservati a 39-41s:
// 30s produceva fallback artificiali. 55s lascia ancora 35s al turno da
// 90s per risposta locale, persistenza e polling, senza un secondo orologio.
export const MARTINA_NATIVE_PROVIDER_TOTAL_BUDGET_MS = 55_000;
/** Un solo passaggio di pianificazione/tool e, se serve, un passaggio finale. */
export const MARTINA_NATIVE_MAX_PROVIDER_PASSES = 2;
/** Limite globale di tool-call emesse dal modello nel path nativo. */
export const MARTINA_NATIVE_MAX_TOOL_CALLS = 3;
// Il lease durevole deve sopravvivere all'intero turno bounded: se scade nello
// stesso istante della deadline, il reaper puo' marcare failed un run mentre
// la chiamata provider sta ancora terminando. Il heartbeat continua a
// rinnovarlo ogni 15s; questo margine copre il confine tra worker e DB.
export const MARTINA_RUN_LEASE_MS = MARTINA_TURN_TOTAL_BUDGET_MS + 30_000;
export const MARTINA_TURN_SERVER_RESPONSE_RESERVE_MS = 750;
export const MARTINA_TURN_CLIENT_RESPONSE_GRACE_MS = 2_000;
export const MARTINA_TURN_COMPACTION_MAX_MS = 4_000;
export const MARTINA_TURN_COMPACTION_RESPONSE_RESERVE_MS = 7_000;

/** Un turno chat interattivo non viene rieseguito dopo lo scadere del budget. */
export const MARTINA_INTERACTIVE_TURN_MAX_ATTEMPTS = 1;

export function interactiveTurnDeadlineAt(nowMs = Date.now()): number {
  return nowMs + MARTINA_TURN_TOTAL_BUDGET_MS;
}

export function readDeadlineAtFromSnapshot(snapshot: unknown): number | undefined {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return undefined;
  const value = (snapshot as { deadlineAt?: unknown }).deadlineAt;
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined;
  return value;
}

export function clampInteractiveDeadlineAt(requested: number | undefined, nowMs = Date.now()): number {
  const fallback = interactiveTurnDeadlineAt(nowMs);
  if (requested === undefined || !Number.isFinite(requested)) return fallback;
  return Math.min(Math.max(Math.trunc(requested), nowMs), fallback);
}

export function isInteractiveTurnBudgetExhausted(snapshot: unknown, nowMs = Date.now()): boolean {
  const deadlineAt = readDeadlineAtFromSnapshot(snapshot);
  return deadlineAt !== undefined && nowMs >= deadlineAt;
}

/** Taglia il provider prima di deadlineAt per lasciare tempo a fallback e persistenza. */
export function interactiveProviderCutoffAt(deadlineAt: number): number {
  return deadlineAt - MARTINA_TURN_SERVER_RESPONSE_RESERVE_MS;
}

/** Fine della finestra in cui il client puo' ancora osservare un fallback persistito. */
export function interactiveClientObserveUntil(deadlineAt: number): number {
  return deadlineAt + MARTINA_TURN_CLIENT_RESPONSE_GRACE_MS;
}
